import type { ReactNode } from 'react';
import { createContext, useCallback, useContext, useMemo, useState } from 'react';
import { type Palette, palettes, type ThemeName, tokens, type as typeScale } from '~/theme';

/**
 * Runtime theme, dark by default with a manual switch to the outdoor palette.
 *
 * Manual, not automatic: an ambient-light sensor would flip the whole interface
 * mid-capture as the operator moves around a vehicle, and a verdict screen that
 * changes colour while being read is worse than one that is simply hard to read.
 * The operator decides once and it stays.
 *
 * Both palettes carry identical key names, so a component reads `c.surface` and never
 * branches on which theme is active. That is the whole point of the token contract —
 * a conditional in a component is a bug waiting for the second theme to diverge.
 */
export interface ThemeValue {
  name: ThemeName;
  /** Colours for the active theme. */
  c: Palette;
  /** Type scale — shared across themes. */
  t: typeof typeScale;
  space: typeof tokens.spacing;
  radius: typeof tokens.radius;
  touch: typeof tokens.touch;
  setTheme: (name: ThemeName) => void;
  toggleTheme: () => void;
}

const ThemeContext = createContext<ThemeValue | null>(null);

export function ThemeProvider({
  children,
  initial = 'light',
}: {
  children: ReactNode;
  initial?: ThemeName;
}) {
  const [name, setName] = useState<ThemeName>(initial);
  const toggleTheme = useCallback(() => setName((n) => (n === 'light' ? 'dark' : 'light')), []);
  const value = useMemo<ThemeValue>(
    () => ({
      name,
      c: palettes[name],
      t: typeScale,
      space: tokens.spacing,
      radius: tokens.radius,
      touch: tokens.touch,
      setTheme: setName,
      toggleTheme,
    }),
    [name, toggleTheme],
  );
  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

/**
 * Falls back to the LIGHT palette outside a provider rather than throwing.
 *
 * Deliberately unlike useAuth, which throws: a missing auth provider is a wiring bug
 * with no safe default, whereas a missing theme provider should not stop a screen
 * rendering in a test or a storybook. The values are real either way.
 */
export function useTheme(): ThemeValue {
  const ctx = useContext(ThemeContext);
  return (
    ctx ?? {
      name: 'light',
      c: palettes.light,
      t: typeScale,
      space: tokens.spacing,
      radius: tokens.radius,
      touch: tokens.touch,
      setTheme: () => {},
      toggleTheme: () => {},
    }
  );
}
